import { useMemo } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { formatCurrency as formatCurrencyBase } from '../utils/formatters';

/**
 * Currency formatting bound to the current tenant's rounding precision.
 *
 * Replaces the boilerplate repeated across many components:
 *   const dp = tenant?.roundingPrecision ?? 3;
 *   const formatCurrency = (amount, currency) => formatCurrencyBase(amount, currency, dp);
 *
 * Precision always comes from the tenant (default 3). The currency comes from the
 * CALLER, because it is almost always contract-/payment-specific (contract.currency_code,
 * payment.currency_code, a prop, …) rather than a tenant default.
 *
 * Two usage shapes:
 *   // currency supplied per call
 *   const { formatCurrency } = useCurrencyFormatter();
 *   formatCurrency(amount, contract.currency_code)
 *
 *   // a single bound currency for the whole component (e.g. a modal's currencyCode prop)
 *   const { formatCurrency } = useCurrencyFormatter(currencyCode);
 *   formatCurrency(amount)   // uses currencyCode; can still be overridden per call
 *
 * @param {string} [boundCurrency] - Optional default currency applied when the returned
 *   formatter is called without an explicit currency argument. Falls back to formatters'
 *   own default ('ZAR') when neither is provided.
 * @returns {{ formatCurrency: (amount: number, currency?: string) => string, dp: number }}
 */
const useCurrencyFormatter = (boundCurrency) => {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;

  return useMemo(() => {
    const formatCurrency = (amount, currency) =>
      formatCurrencyBase(amount, currency !== undefined ? currency : boundCurrency, dp);
    return { formatCurrency, dp };
  }, [dp, boundCurrency]);
};

export default useCurrencyFormatter;
